/* ============================================================
   SAM-591 - unit pricing helpers (pure, no React).

   Mirrors functions/booking/pricing.py (sam-lambda-functions) for the admin's live quote and
   validation; the backend is the authority and re-prices every booking. Rules:
     total = unit price + sum of extra_guest_price over the guests beyond included_pax
     the included slots go to the MOST EXPENSIVE extra_guest_price first (favours the customer;
     ties keep option order), so the result never depends on the order guests are listed in
     total pax above max_pax is rejected
   ============================================================ */
import type { BookingOptionForm } from '../../hooks/useServiceEditor';
import type { ResourceForm } from '../../components/ServiceResourcesEditor';

export interface UnitConfig {
  price: number;
  included_pax: number;
  max_pax: number | null;
}

export interface UnitDefaultsForm {
  unit_price: string;
  unit_included_pax: string;
  unit_max_pax: string;
}

const num = (v: string | undefined): number | null => {
  if (v == null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Effective unit config for one resource: service defaults overridden key-by-key. Null when no
 * price is configured anywhere (the backend rejects that with `unit_price_missing`). */
export function resolveUnitConfig(defaults: UnitDefaultsForm, resource?: ResourceForm): UnitConfig | null {
  const price = num(resource?.price) ?? num(defaults.unit_price);
  if (price == null || price < 0) return null;
  return {
    price,
    included_pax: num(resource?.included_pax) ?? num(defaults.unit_included_pax) ?? 0,
    max_pax: num(resource?.max_pax) ?? num(defaults.unit_max_pax),
  };
}

export type UnitQuote =
  | { ok: true; total: number; extras: Array<{ id: string; qty: number; each: number }>; pax: number }
  | { ok: false; error: 'max_pax_exceeded' | 'no_guests'; max_pax?: number; pax: number };

/** Quote for `guests` (option id -> qty) on a unit. `options` supplies each type's extra price. */
export function quoteUnit(
  unit: UnitConfig,
  options: Array<Pick<BookingOptionForm, 'id' | 'extra_guest_price'>>,
  guests: Record<string, number>,
): UnitQuote {
  const pax = options.reduce((sum, o) => sum + Math.max(0, guests[o.id] || 0), 0);
  if (pax <= 0) return { ok: false, error: 'no_guests', pax };
  if (unit.max_pax != null && pax > unit.max_pax) {
    return { ok: false, error: 'max_pax_exceeded', max_pax: unit.max_pax, pax };
  }
  const priced = options.map((o, order) => ({
    id: o.id, order, qty: Math.max(0, guests[o.id] || 0), each: num(o.extra_guest_price) ?? 0,
  }));
  priced.sort((a, b) => b.each - a.each || a.order - b.order);
  let slots = unit.included_pax;
  let total = unit.price;
  const extras: Array<{ id: string; qty: number; each: number }> = [];
  for (const p of priced) {
    const included = Math.min(p.qty, slots);
    slots -= included;
    const extra = p.qty - included;
    if (extra > 0) {
      extras.push({ id: p.id, qty: extra, each: p.each });
      total += extra * p.each;
    }
  }
  return { ok: true, total: Math.round(total * 100) / 100, extras, pax };
}

/** Save-time problems for a unit-priced service (empty = fine). */
export function unitPricingIssues(
  defaults: UnitDefaultsForm,
  resources: ResourceForm[],
  options: BookingOptionForm[],
): string[] {
  const issues: string[] = [];
  const rows = resources.length ? resources : [undefined];
  rows.forEach((r) => {
    const label = r ? (r.name || r.key) : 'The unit';
    const cfg = resolveUnitConfig(defaults, r);
    if (!cfg) {
      issues.push(`${label} has no price - set a unit price or a per-unit override.`);
    } else if (cfg.max_pax != null && cfg.included_pax > cfg.max_pax) {
      issues.push(`${label}: included pax (${cfg.included_pax}) is above max pax (${cfg.max_pax}).`);
    }
  });
  options.filter((o) => o.label_en.trim()).forEach((o) => {
    if (num(o.extra_guest_price) == null || (num(o.extra_guest_price) as number) < 0) {
      issues.push(`${o.label_en}: set an extra-guest price of 0 or more.`);
    }
  });
  return issues;
}

/** Units whose physical capacity is below the max pax the pricing promises (booking would price
 * fine and then be rejected by the booking engine) - the unit-mode twin of headcountWarning. */
export function unitsBelowMaxPax(
  defaults: UnitDefaultsForm,
  resources: ResourceForm[],
): Array<{ name: string; capacity: number; max_pax: number }> {
  const out: Array<{ name: string; capacity: number; max_pax: number }> = [];
  resources.forEach((r) => {
    const cfg = resolveUnitConfig(defaults, r);
    const capacity = num(r.capacity);
    if (cfg?.max_pax != null && capacity != null && capacity < cfg.max_pax) {
      out.push({ name: r.name || r.key, capacity, max_pax: cfg.max_pax });
    }
  });
  return out;
}

/**
 * Switching a per-person Cabana to "Per unit": the old per-person setup sold the cabana itself as
 * a booking option booked exactly once (min = max = 1, e.g. "Cabana rental") plus a guest line.
 * That line becomes the unit price and the other options keep their per-person price as their
 * extra-guest price. Returns null when there is nothing to carry over (unit price already set,
 * or no single min = max = 1 line next to at least one guest line).
 */
export function carryOverToUnit<T extends Pick<BookingOptionForm, 'label_en' | 'label_es' | 'price' | 'min_amount' | 'max_amount' | 'extra_guest_price'>>(
  unitPrice: string,
  options: T[],
): { unit_price: string; booking_options: T[]; moved: string } | null {
  if ((unitPrice ?? '').trim() !== '') return null;
  const isUnitLine = (o: T) => Number(o.min_amount) === 1 && Number(o.max_amount) === 1 && (o.price ?? '').trim() !== '';
  const unitLines = options.filter(isUnitLine);
  if (unitLines.length !== 1 || options.length < 2) return null;
  const line = unitLines[0];
  return {
    unit_price: line.price,
    booking_options: options
      .filter((o) => o !== line)
      .map((o) => ((o.extra_guest_price ?? '').trim() === '' ? { ...o, extra_guest_price: o.price } : o)),
    moved: line.label_en || line.label_es || 'the unit line',
  };
}
